(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const animationsEnabled = document.body.dataset.animationsEnabled !== 'false';
  const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  let scrollObserver = null;
  let staggerObserver = null;

  function queryAll(scope, selector) {
    if (!scope || scope === document) {
      return document.querySelectorAll(selector);
    }
    const matches = [];
    if (scope.matches && scope.matches(selector)) matches.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(selector).forEach((el) => matches.push(el));
    }
    return matches;
  }

  function reveal(el) {
    el.classList.add('is-visible');
  }

  function isInViewport(el) {
    const rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight * 0.92 && rect.bottom > 0;
  }

  function initScrollAnimations(root) {
    const elements = queryAll(root || document, '.animate-on-scroll');

    if (prefersReducedMotion || !animationsEnabled) {
      elements.forEach(reveal);
      return;
    }

    document.documentElement.classList.add('js-animations');
    if (!elements.length) return;

    if (!scrollObserver) {
      scrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal(entry.target);
              scrollObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
    }

    elements.forEach((el) => {
      if (isDesignMode) {
        reveal(el);
        return;
      }
      if (el.classList.contains('is-visible')) return;
      if (isInViewport(el)) {
        reveal(el);
        return;
      }
      scrollObserver.observe(el);
    });
  }

  function initStaggerAnimations(root) {
    const containers = queryAll(root || document, '[data-stagger-cards]');

    if (prefersReducedMotion || !animationsEnabled) {
      containers.forEach(reveal);
      return;
    }

    document.documentElement.classList.add('js-animations');
    if (!containers.length) return;

    if (!staggerObserver) {
      staggerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              reveal(entry.target);
              staggerObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
      );
    }

    containers.forEach((el) => {
      if (isDesignMode) {
        reveal(el);
        return;
      }
      if (el.classList.contains('is-visible')) return;
      if (isInViewport(el)) {
        reveal(el);
        return;
      }
      staggerObserver.observe(el);
    });
  }

  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const drawer = document.querySelector('[data-mobile-menu-drawer]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    const closeButtons = document.querySelectorAll('[data-mobile-menu-close]');
    if (!toggle || !drawer) return;

    const close = () => {
      drawer.hidden = true;
      if (overlay) overlay.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
    };

    const open = () => {
      drawer.hidden = false;
      if (overlay) overlay.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mobile-menu-open');
      document.body.style.overflow = 'hidden';
      const closeButton = drawer.querySelector('[data-mobile-menu-close]');
      if (closeButton) closeButton.focus();
    };

    toggle.addEventListener('click', () => {
      if (toggle.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', close);
    });

    if (overlay) overlay.addEventListener('click', close);

    drawer.querySelectorAll('a[href]').forEach((link) => {
      link.addEventListener('click', close);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        close();
        toggle.focus();
      }
    });
  }

  function initHeaderSearch() {
    const wrap = document.querySelector('[data-header-search-wrap]');
    const toggle = document.querySelector('[data-header-search-toggle]');
    const panel = document.querySelector('[data-header-search-panel]');
    if (!toggle || !panel) return;

    const input = panel.querySelector('[data-header-search-input]');

    const close = () => {
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (wrap) wrap.classList.remove('is-open');
      document.body.classList.remove('header-search-open');
    };

    const open = () => {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (wrap) wrap.classList.add('is-open');
      document.body.classList.add('header-search-open');
      if (input) {
        window.requestAnimationFrame(() => input.focus());
      }
    };

    toggle.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (toggle.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    document.addEventListener('click', (event) => {
      if (panel.hidden) return;
      if (panel.contains(event.target) || toggle.contains(event.target)) return;
      if (wrap && wrap.contains(event.target)) return;
      close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
        close();
        toggle.focus();
      }
    });
  }

  function initFaqAccordion() {
    document.querySelectorAll('[data-faq-accordion]').forEach((accordion) => {
      const oneOpen = accordion.dataset.oneOpen === 'true';

      accordion.querySelectorAll('[data-faq-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const panel = trigger.nextElementSibling;
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';

          if (oneOpen) {
            accordion.querySelectorAll('[data-faq-trigger]').forEach((t) => {
              t.setAttribute('aria-expanded', 'false');
              t.nextElementSibling.hidden = true;
            });
          }

          trigger.setAttribute('aria-expanded', String(!isOpen));
          panel.hidden = isOpen;
        });
      });
    });
  }

  function initCarousels() {
    document.querySelectorAll('[data-carousel]').forEach((carousel) => {
      if (carousel.dataset.carouselReady === 'true') return;

      const track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;

      const markedSlides = carousel.querySelectorAll('[data-carousel-slide]');
      const slides = markedSlides.length ? Array.from(markedSlides) : Array.from(track.children);
      const prevBtn = carousel.querySelector('[data-carousel-prev]');
      const nextBtn = carousel.querySelector('[data-carousel-next]');
      const dotsContainer = carousel.querySelector('[data-carousel-dots]');
      const controls = carousel.querySelector('.testimonials__controls, .products-grid__controls');

      if (!slides.length) {
        if (controls) controls.hidden = true;
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
        if (dotsContainer) dotsContainer.hidden = true;
        return;
      }

      let index = 0;
      const autoplay = carousel.dataset.autoplay === 'true';
      const loop = carousel.dataset.loop !== 'false';
      const speed = parseInt(carousel.dataset.autoplaySpeed || '5000', 10);
      let timer;

      const slidePercent = () => {
        const parentWidth = track.parentElement.getBoundingClientRect().width;
        const slideWidth = slides[0].getBoundingClientRect().width;
        if (!parentWidth || !slideWidth) return 100;
        return (slideWidth / parentWidth) * 100;
      };

      const visibleCount = () => Math.max(1, Math.round(100 / slidePercent()));
      const maxIndex = () => Math.max(0, slides.length - visibleCount());
      const canScroll = () => maxIndex() > 0;

      const syncControls = () => {
        const scrollable = canScroll();
        if (controls) controls.hidden = !scrollable;
        if (prevBtn) {
          prevBtn.hidden = !scrollable;
          prevBtn.disabled = !scrollable;
        }
        if (nextBtn) {
          nextBtn.hidden = !scrollable;
          nextBtn.disabled = !scrollable;
        }
        if (dotsContainer) dotsContainer.hidden = !scrollable;
        if (!scrollable) {
          index = 0;
          track.style.transform = 'translateX(0)';
        }
      };

      const goTo = (i) => {
        const max = maxIndex();
        if (max <= 0) {
          index = 0;
          track.style.transform = 'translateX(0)';
          syncControls();
          return;
        }

        if (loop) {
          index = ((i % (max + 1)) + (max + 1)) % (max + 1);
        } else {
          index = Math.max(0, Math.min(i, max));
        }

        track.style.transform = `translateX(-${index * slidePercent()}%)`;

        if (dotsContainer) {
          dotsContainer.querySelectorAll('[data-carousel-dot]').forEach((dot, di) => {
            dot.setAttribute('aria-current', di === index ? 'true' : 'false');
          });
        }

        if (!loop) {
          if (prevBtn) prevBtn.disabled = index <= 0;
          if (nextBtn) nextBtn.disabled = index >= max;
        }
      };

      if (dotsContainer && !dotsContainer.children.length) {
        const pageCount = () => maxIndex() + 1;
        const buildDots = () => {
          dotsContainer.innerHTML = '';
          for (let i = 0; i < pageCount(); i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'carousel__dot';
            dot.dataset.carouselDot = '';
            dot.setAttribute('aria-label', `Slide ${i + 1}`);
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
            dot.addEventListener('click', () => goTo(i));
            dotsContainer.appendChild(dot);
          }
        };
        buildDots();
        carousel._rebuildDots = buildDots;
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(index - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(index + 1));

      const onResize = () => {
        if (carousel._rebuildDots) carousel._rebuildDots();
        syncControls();
        goTo(index);
      };
      window.addEventListener('resize', onResize);

      syncControls();
      requestAnimationFrame(() => {
        syncControls();
        goTo(0);
      });

      if (autoplay && !prefersReducedMotion) {
        const start = () => {
          clearInterval(timer);
          if (!canScroll()) return;
          timer = setInterval(() => goTo(index + 1), speed);
        };
        const stop = () => clearInterval(timer);
        start();
        carousel.addEventListener('mouseenter', stop);
        carousel.addEventListener('mouseleave', start);
      }

      carousel.dataset.carouselReady = 'true';
    });
  }

  function cartRoot() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  function goToCheckout() {
    let form = document.querySelector('[data-cart-checkout-form]');

    if (!form) {
      form = document.createElement('form');
      form.method = 'POST';
      form.action = cartRoot() + 'cart';
      form.hidden = true;
      form.setAttribute('data-cart-checkout-form', '');

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '';
      form.appendChild(input);
      document.body.appendChild(form);
    }

    form.submit();
  }

  function initCheckoutRedirect() {
    if (!/(?:^|[?&])checkout(?:[=&]|$)/.test(window.location.search)) return;

    try {
      sessionStorage.setItem('tpa_booking_scheduled', '1');
    } catch (error) {
      /* ignore */
    }

    goToCheckout();
  }

  function initGoCheckoutButtons() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-go-checkout]');
      if (!trigger) return;
      event.preventDefault();
      goToCheckout();
    });
  }

  window.TPA = window.TPA || {};
  window.TPA.goToCheckout = goToCheckout;

  function ajaxFormLabel(form) {
    const button = form.querySelector('[type="submit"]');
    if (!button) return null;
    return button.querySelector('[data-atc-label], .btn__text') || button;
  }

  function setAjaxLabel(form, key) {
    const label = ajaxFormLabel(form);
    const button = form.querySelector('[type="submit"]');
    if (!label || !button) return;
    const text = button.getAttribute('data-' + key) || label.getAttribute('data-' + key);
    if (text) label.textContent = text;
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.hidden = count < 1;
      badge.textContent = String(count);
    });
  }

  function formatMoney(cents, moneyFormat) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents, moneyFormat);
    return (Number(cents) / 100).toFixed(2);
  }

  function toastRoot() {
    return document.querySelector('[data-cart-toast]');
  }

  let toastTimer = null;

  function hideCartToast() {
    const toast = toastRoot();
    if (!toast) return;
    toast.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!toast.classList.contains('is-visible')) toast.hidden = true;
    }, 280);
  }

  function showCartToast(payload) {
    const toast = toastRoot();
    if (!toast || !payload) return;

    const media = toast.querySelector('[data-cart-toast-media]');
    const image = toast.querySelector('[data-cart-toast-image]');
    const eyebrow = toast.querySelector('[data-cart-toast-eyebrow]');
    const title = toast.querySelector('[data-cart-toast-title]');
    const meta = toast.querySelector('[data-cart-toast-meta]');
    const link = toast.querySelector('[data-cart-toast-link]');
    const moneyFormat = toast.getAttribute('data-money-format');
    const addedLabel = toast.getAttribute('data-added-label') || '';
    const errorLabel = toast.getAttribute('data-error-label') || '';

    toast.classList.toggle('cart-toast--error', Boolean(payload.error));
    if (eyebrow) {
      eyebrow.textContent = addedLabel;
      eyebrow.hidden = Boolean(payload.error);
    }
    if (title) {
      title.textContent = payload.error ? payload.message || errorLabel : payload.title || '';
      title.hidden = !title.textContent;
    }

    if (meta) {
      const parts = [];
      if (!payload.error) {
        if (payload.variant && payload.variant !== 'Default Title') parts.push(payload.variant);
        if (payload.price != null) parts.push(formatMoney(payload.price, moneyFormat));
      }
      meta.textContent = parts.join(' · ');
      meta.hidden = !meta.textContent;
    }

    if (link) link.hidden = Boolean(payload.error);

    if (media && image) {
      if (!payload.error && payload.image) {
        image.src = payload.image;
        image.alt = payload.title || '';
        media.hidden = false;
      } else {
        image.removeAttribute('src');
        media.hidden = true;
      }
    }

    toast.hidden = false;
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    if (toastTimer) window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideCartToast, payload.error ? 4200 : 5000);
  }

  function toastFromItem(item) {
    return {
      title: item.product_title || item.title || '',
      variant: item.variant_title || '',
      image: item.image || '',
      price: item.final_price != null ? item.final_price : item.price
    };
  }

  function cartHasService(cart) {
    const serviceHandles = window.__tpaServiceHandles || [];
    return (cart.items || []).some((item) => {
      const type = String(item.product_type || '').toLowerCase();
      if (type === 'service') return true;
      return serviceHandles.includes(item.handle);
    });
  }

  function formIsAddon(form) {
    if (form.classList.contains('addon-upsell__form')) return true;
    if (form.querySelector('[data-addon-product]')) return true;
    const addonHandles = window.__tpaAddonHandles || [];
    const productHandle = form.getAttribute('data-product-handle');
    return Boolean(productHandle && addonHandles.includes(productHandle));
  }

  function formIsService(form) {
    return (
      form.getAttribute('data-service-form') === 'true' || Boolean(form.closest('.product-form--service'))
    );
  }

  function getSelectedProductAddonIds(form) {
    const section = form.closest('[data-product-section]') || form.closest('.shopify-section');
    if (!section) return [];
    const list = section.querySelector('[data-selected-addons-list]');
    if (!list) return [];
    return Array.from(list.querySelectorAll('[data-selected-addon-variant]')).map((input) =>
      Number(input.value)
    );
  }

  function fetchCart() {
    return fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' }
    }).then((response) => {
      if (!response.ok) throw new Error('Cart error');
      return response.json();
    });
  }

  function addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    const formData = new FormData(form);
    const isAddon = formIsAddon(form);
    const addonRequired =
      (button && button.getAttribute('data-addon-required')) ||
      'Add-ons can only be purchased with a consulting service.';

    if (button) button.disabled = true;
    setAjaxLabel(form, 'adding');

    const postForm = () =>
      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      }).then((response) => response.json().then((data) => ({ ok: response.ok, data })));

    const postItems = (items) =>
      fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      }).then((response) => response.json().then((data) => ({ ok: response.ok, data })));

    const addRequest = (() => {
      if (isAddon) {
        return fetchCart().then((cart) => {
          if (cartHasService(cart)) return postForm();
          throw new Error(addonRequired);
        });
      }

      if (formIsService(form)) {
        const serviceId = Number(formData.get('id'));
        const quantity = Number(formData.get('quantity') || 1);
        const addonIds = getSelectedProductAddonIds(form);
        if (addonIds.length) {
          const items = [{ id: serviceId, quantity }];
          addonIds.forEach((id) => items.push({ id, quantity: 1 }));
          return postItems(items);
        }
        return postForm();
      }

      return postForm();
    })();

    return addRequest
      .then(({ ok, data }) => {
        if (!ok) {
          throw new Error(data.description || data.message || 'Cart error');
        }
        const addedItem = Array.isArray(data.items) ? data.items[data.items.length - 1] : data;
        return fetchCart().then((cart) => ({ item: addedItem, cart }));
      })
      .then(({ item, cart }) => {
        const toastItem = toastFromItem(item);
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        if (document.querySelector('[data-cart-page]')) {
          try {
            sessionStorage.setItem('cart-toast', JSON.stringify(toastItem));
          } catch (error) {
            /* ignore */
          }
          window.location.reload();
          return;
        }
        showCartToast(toastItem);
        setAjaxLabel(form, 'added');
        window.setTimeout(() => {
          setAjaxLabel(form, 'add');
          if (button) button.disabled = false;
        }, 1600);
      })
      .catch((error) => {
        const fallback = (button && button.getAttribute('data-error')) || '';
        const apiMessage = error && error.message && error.message !== 'Cart error' ? error.message : '';
        showCartToast({ error: true, message: apiMessage || fallback });
        setAjaxLabel(form, 'error');
        window.setTimeout(() => {
          setAjaxLabel(form, 'add');
          if (button) button.disabled = false;
        }, 2200);
      });
  }

  function initCartToast() {
    const toast = toastRoot();
    if (!toast) return;
    const close = toast.querySelector('[data-cart-toast-close]');
    if (close) close.addEventListener('click', hideCartToast);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !toast.hidden) hideCartToast();
    });
    try {
      const pending = sessionStorage.getItem('cart-toast');
      if (pending) {
        sessionStorage.removeItem('cart-toast');
        showCartToast(JSON.parse(pending));
      }
    } catch (error) {
      /* ignore */
    }
  }

  function initProductAddonSelection(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const sections =
      scope.matches && scope.matches('[data-product-section], .shopify-section')
        ? [scope]
        : Array.from(scope.querySelectorAll('[data-product-section], .shopify-section'));

    sections.forEach((productSection) => {
      if (!productSection.querySelector('.product-form--service')) return;

      const panel = productSection.querySelector('[data-selected-addons-panel]');
      const list = productSection.querySelector('[data-selected-addons-list]');
      if (!panel || !list || panel.dataset.addonSelectionReady) return;
      panel.dataset.addonSelectionReady = 'true';

      const moneyFormat = productSection.getAttribute('data-money-format');
      const removeLabel = panel.getAttribute('data-remove-label') || 'Remove add-on';
      const selected = new Map();

      function formatMoney(cents) {
        if (window.Shopify && Shopify.formatMoney) {
          return Shopify.formatMoney(Number(cents), moneyFormat);
        }
        return (Number(cents) / 100).toFixed(2);
      }

      function getCardSelection(card) {
        const input = card.querySelector(
          '[data-addon-variant-input]:checked, input[type="hidden"][data-addon-variant-input]'
        );
        const btn = card.querySelector('[data-select-addon]');
        if (!input || !btn) return null;

        const title = btn.getAttribute('data-addon-title') || '';
        const variantTitle = input.getAttribute('data-variant-title');
        let label = title;
        if (variantTitle && variantTitle !== 'Default Title') {
          label = title + ' · ' + variantTitle;
        }

        return {
          productId: card.getAttribute('data-addon-product-id'),
          variantId: Number(input.value),
          title: label,
          price: Number(input.getAttribute('data-variant-price') || btn.getAttribute('data-addon-price'))
        };
      }

      function syncCards() {
        productSection.querySelectorAll('[data-addon-select-card]').forEach((card) => {
          const productId = card.getAttribute('data-addon-product-id');
          const isSelected = selected.has(productId);
          card.classList.toggle('is-selected', isSelected);
          const btn = card.querySelector('[data-select-addon]');
          if (!btn) return;
          btn.classList.toggle('is-selected', isSelected);
          const label = btn.querySelector('.btn__text');
          if (label) {
            label.textContent = isSelected ? btn.getAttribute('data-selected') : btn.getAttribute('data-add');
          }
        });
      }

      function renderSelected() {
        list.innerHTML = '';
        selected.forEach((item) => {
          const li = document.createElement('li');
          li.className = 'product-selected-addons__item';

          const copy = document.createElement('div');
          copy.className = 'product-selected-addons__copy';

          const title = document.createElement('span');
          title.className = 'product-selected-addons__title';
          title.textContent = item.title;

          const price = document.createElement('span');
          price.className = 'product-selected-addons__price';
          price.textContent = '+ ' + formatMoney(item.price);

          copy.appendChild(title);
          copy.appendChild(price);

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'product-selected-addons__remove';
          remove.setAttribute('aria-label', removeLabel);
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            selected.delete(item.productId);
            renderSelected();
          });

          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.setAttribute('data-selected-addon-variant', '');
          hidden.value = String(item.variantId);

          li.appendChild(copy);
          li.appendChild(remove);
          li.appendChild(hidden);
          list.appendChild(li);
        });

        panel.hidden = selected.size === 0;
        syncCards();
      }

      productSection.querySelectorAll('[data-select-addon]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const card = btn.closest('[data-addon-select-card]');
          if (!card) return;
          const item = getCardSelection(card);
          if (!item) return;

          if (selected.has(item.productId)) {
            selected.delete(item.productId);
          } else {
            selected.set(item.productId, item);
          }
          renderSelected();
        });
      });

      productSection.querySelectorAll('[data-addon-variant-input]').forEach((input) => {
        input.addEventListener('change', () => {
          const card = input.closest('[data-addon-select-card]');
          if (!card) return;
          const productId = card.getAttribute('data-addon-product-id');
          if (!selected.has(productId)) return;
          const item = getCardSelection(card);
          if (item) {
            selected.set(productId, item);
            renderSelected();
          }
        });
      });
    });
  }

  function initAjaxCartAdd() {
    if (document.documentElement.dataset.ajaxCartReady) return;
    document.documentElement.dataset.ajaxCartReady = 'true';

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!form || !form.matches) return;
      if (!form.matches('.product-form__form, .addon-upsell__form, .product-card__form')) return;
      event.preventDefault();
      if (form.dataset.ajaxBusy === 'true') return;
      form.dataset.ajaxBusy = 'true';
      addToCart(form).finally(() => {
        if (!document.querySelector('[data-cart-page]')) {
          form.dataset.ajaxBusy = 'false';
        }
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initCheckoutRedirect();
    initGoCheckoutButtons();
    initScrollAnimations();
    initStaggerAnimations();
    initMobileMenu();
    initHeaderSearch();
    initFaqAccordion();
    initCarousels();
    initAjaxCartAdd();
    initProductAddonSelection();
    initCartToast();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initScrollAnimations(event.target);
    initStaggerAnimations(event.target);
    initFaqAccordion();
    initCarousels();
    initProductAddonSelection(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.shopify-section') || event.target;
    initScrollAnimations(section);
    initStaggerAnimations(section);
  });
})();
